import { useState, useMemo } from "react";
import { Bell } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/empty-state";
import { NotificationStatusBadge } from "@/components/notification-status-badge";
import { EntityButton } from "@/components/entity-button";
import { TableSkeleton } from "@/components/loading-skeletons";
import { PageHeader } from "@/components/layout/page-header";
import { SearchInput } from "@/components/search-input";
import { SortButton } from "@/components/sort-button";
import { RowLink, TableLinkRow } from "@/components/table-link-row";
import { useNotificationsQuery } from "@/hooks/use-notifications";
import { formatDate } from "@/lib/utils";
import type { Notification } from "@/types/notification";

const AUTO_REFRESH_INTERVAL = 30000;

type SortField = "message" | "status" | "scheduledAt";
type SortOrder = "asc" | "desc";

const STATUS_ORDER: Record<Notification["status"], number> = {
  created: 0,
  queued: 1,
  completed: 2,
  failed: 3,
  skipped: 4,
};

function getDisplayText(notification: Notification): string {
  return notification.payload.title;
}

export function NotificationsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("message");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const {
    data: notifications,
    isLoading,
    error,
  } = useNotificationsQuery({
    refetchInterval: AUTO_REFRESH_INTERVAL,
  });

  const filteredAndSortedNotifications = useMemo(() => {
    if (!notifications) return [];

    let result = notifications;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((notification) =>
        getDisplayText(notification).toLowerCase().includes(query)
      );
    }

    result = [...result].sort((a, b) => {
      let comparison: number;
      if (sortField === "message") {
        comparison = getDisplayText(a).localeCompare(getDisplayText(b));
      } else if (sortField === "scheduledAt") {
        comparison = a.scheduled_at.localeCompare(b.scheduled_at);
      } else {
        comparison = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [notifications, searchQuery, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortDirection = (field: SortField) =>
    sortField === field ? sortOrder : null;

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        title="Notifications"
        actions={
          notifications && (
            <EntityButton entityName="Notification" to="/notifications/new" />
          )
        }
      />

      <div className="flex flex-1 flex-col">
        {notifications && notifications.length > 0 && (
          <div className="border-b pb-4">
            <SearchInput
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        )}

        {isLoading && <TableSkeleton />}

        {error && (
          <p className="py-16 text-center text-sm text-destructive">
            Failed to load notifications
          </p>
        )}

        {notifications && (
          <>
            {filteredAndSortedNotifications.length === 0 && (
              <EmptyState
                icon={Bell}
                title={searchQuery.trim() ? "No results" : "No notifications found"}
                description={
                  searchQuery.trim()
                    ? "No notifications match your search."
                    : "Notifications you send will appear here."
                }
              >
                {!searchQuery.trim() && (
                  <EntityButton entityName="Notification" to="/notifications/new" />
                )}
              </EmptyState>
            )}

            {filteredAndSortedNotifications.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortButton
                        label="Message"
                        direction={getSortDirection("message")}
                        onClick={() => handleSort("message")}
                      />
                    </TableHead>
                    <TableHead className="md:w-48">
                      <SortButton
                        label="Scheduled"
                        direction={getSortDirection("scheduledAt")}
                        onClick={() => handleSort("scheduledAt")}
                      />
                    </TableHead>
                    <TableHead className="md:w-44">
                      <SortButton
                        label="Status"
                        direction={getSortDirection("status")}
                        onClick={() => handleSort("status")}
                      />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedNotifications.map((notification) => (
                    <TableLinkRow
                      key={notification.id}
                      to={`/notifications/${notification.id}`}
                    >
                      <TableCell>
                        <RowLink to={`/notifications/${notification.id}`}>
                          {getDisplayText(notification)}
                        </RowLink>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDate(notification.scheduled_at)}
                      </TableCell>
                      <TableCell>
                        <NotificationStatusBadge status={notification.status} />
                      </TableCell>
                    </TableLinkRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
